import { parsePrice, normalizePriceOfferToInternationalFormat } from "@/utils/common/price";
import { getFullAEMStaticSrc } from "@/utils/htmlUtils";
import { getCustoStampByOffer } from "../devices-list";

/**
 * Función que recibes las ofertas y el aliasUrl para obtener la capacidad y el color
 * @param {Object} offers
 * @param {string} aliasUrl
 * @returns Capacidad y color en base al aliasUrl
 */
export const findKeysWithAliasUrl = (offers, aliasUrl) => {
  let capacityColor = null;
  Object.entries(offers).forEach(([key]) => {
    let capacity = key;
    const objectCapacity = offers[key];
    Object.entries(objectCapacity).forEach(([key]) => {
      if (objectCapacity[key]?.customAliasUrl === aliasUrl ||
        objectCapacity[key].aliasUrl === aliasUrl ||
        objectCapacity[key]?.cards?.cardR2R[0]?.aliasUrl === aliasUrl ||
        objectCapacity[key]?.cards?.cardAditionalR2R[0]?.aliasUrl === aliasUrl ||
        objectCapacity[key]?.cards?.cardFree[0]?.aliasUrl === aliasUrl ||
        objectCapacity[key]?.cards?.cardR2R[0]?.customAliasUrl === aliasUrl ||
        objectCapacity[key]?.cards?.cardAditionalR2R[0]?.customAliasUrl === aliasUrl ||
        objectCapacity[key]?.cards?.cardFree[0]?.customAliasUrl === aliasUrl
      ) {
        capacityColor = { capacity, color: key, name: objectCapacity[key].name };
      }
    });
  });

  if (!capacityColor) {
    console.warn(`No se ha encontrado capacityColor del alias: ${aliasUrl} con offers: ${JSON.stringify(offers)}`)
  }

  return capacityColor;
};

/**
 * Recibe una capacidad y la tranforma a TB en caso de que sea necesario
 * @param {string} capacity
 * @returns Capacidad normalizada
 */
export const normalizeCapacity = (capacity) => {
  if (capacity) {
    const numberGigabytes = parseInt(capacity, 10);
    if (numberGigabytes >= 1024) {
      const terabytes = numberGigabytes / 1024;
      return `${terabytes} TB`;
    }
    return capacity;
  }
  return null;
};

/**
 * Recibe dos capacidades (que pueden ser tallas, etc) y las ordena
 * @param {string} capacityOne
 * @param {string} capacityTwo
 * @returns Capacidades ordenadas
 */
export const compareCapacities = (capacityOne, capacityTwo) => {
  let valueCapacityOne = parseFloat(capacityOne);
  let valueCapacityTwo = parseFloat(capacityTwo);
  //Son capacidades numericas
  if (!Number.isNaN(valueCapacityOne) && !Number.isNaN(valueCapacityTwo)) {
    const unityCapacityOne = capacityOne.slice(-2).toUpperCase();
    const unityCapacityTwo = capacityTwo.slice(-2).toUpperCase();

    if (unityCapacityOne === "TB") {
      valueCapacityOne *= 1024;
    }

    if (unityCapacityTwo === "TB") {
      valueCapacityTwo *= 1024;
    }
  } else {
    //Cajon desastre para tallas, potencias etc.
    // Usamos una expresión regular para capturar solo el número antes del guion. Ej: T6-16,5 mm o solamente T6
    const regex = /[^\d]*(\d+)-?/;
    valueCapacityOne = capacityOne.match(regex)?.[1];
    valueCapacityTwo = capacityTwo.match(regex)?.[1];
  }
  //Salvavidas por si no ha conseguido calcular valores
  if (!valueCapacityOne || !valueCapacityTwo) {
    valueCapacityOne = 1;
    valueCapacityTwo = 2;
  }
  return valueCapacityOne - valueCapacityTwo;
};

export const hasOffer = ({ array, obj, offerType, offerProfile, custoStamps }) => {
  if (array && obj) {
    // eslint-disable-next-line no-unused-vars
    Object.entries(obj).forEach(([key, value]) => {
      array.push({
        ...value,
        // monthlyPayment24: 52.20,
        // monthlyPayment36: 28.50,
        // monthlyPayment48: 25.50,
        storage: normalizeCapacity(value.storage),
        pvp_fusion_av: normalizePriceOfferToInternationalFormat(value.pvp_fusion_av),
        pvp_fusion_mv: normalizePriceOfferToInternationalFormat(value.pvp_fusion_mv),
        pvp_fusion_bv: normalizePriceOfferToInternationalFormat(value.pvp_fusion_bv),
        pvp_fusion_av_promo: normalizePriceOfferToInternationalFormat(value.pvp_fusion_av_promo),
        pvp_fusion_mv_promo: normalizePriceOfferToInternationalFormat(value.pvp_fusion_mv_promo),
        pvp_fusion_bv_promo: normalizePriceOfferToInternationalFormat(value.pvp_fusion_bv_promo),
        fundingOfferInfo: value.fundingOfferInfo.sort((a, b) => {
          return b.months - a.months;
        }),
        custoStamp: getCustoStampByOffer(value, custoStamps) || null,
        offerType: offerType || null,
        offerProfile: offerProfile || null
      });
    });
  }
};

/**
 * Recibe la oferta, la capacidad y el color
 * Se crean tres arrays para cada una de las card de oferta (r2r, r2rAdicional y libre)
 * Dentro de estas cards se encontrara en la posicion 0 la oferta de dispositivo NUEVO y el la posicion 1 la oferta de REACONDICONADOS
 * Se recorre la oferta en base a la capacidad y color, y se van rellenando cada unas de las cards en base al tipo (r2r, r2rAdicional y libre)
 * @param {*} offers
 * @param {*} capacity
 * @param {*} color
 * @returns Cards para pintar cada uno de los BuyBox
 */
export const composeCardByClientType = (offers, capacity, color, custoStamps) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    const cardR2R = [];
    const cardAditionalR2R = [];
    const cardFree = [];
    Object.entries(mainOffer).forEach(([key, value]) => {
      switch (key) {
        case "r2r":
          hasOffer({ array: cardR2R, obj: value, offerType: key, custoStamps });
          break;
        case "r2rAdditional":
          hasOffer({ array: cardAditionalR2R, obj: value, offerType: key, custoStamps });
          break;
        case "libre":
          hasOffer({ array: cardFree, obj: value, offerType: key, custoStamps });
          break;
        case "r2rReacondicionado":
          hasOffer({ array: cardR2R, obj: value, offerType: key, custoStamps });
          break;
        case "r2rAdditionalReacondicionado":
          hasOffer({ array: cardAditionalR2R, obj: value, offerType: key, custoStamps });
          break;
        case "reacondicionado":
          hasOffer({ array: cardFree, obj: value, offerType: key, custoStamps });
          break;
        default:
          break;
      }
      if (key.startsWith("r2r_")) {
        const offerType = key.split("_")[0];
        const offerProfile = key.split("_")[1];
        hasOffer({ array: cardR2R, obj: value, offerType, offerProfile, custoStamps });
      };
    });
    return {
      cardR2R,
      cardAditionalR2R,
      cardFree,
    };
  }
  return null;
};

/**
 * Recibe la oferta, la capacidad y el color
 * Recorre cada tipo de oferta (r2r, r2rAdicional, libre, reacondicionados) y unifica el aliasUrl
 * @param {Object} offers
 * @param {string} capacity
 * @param {string} color
 * @returns String con el aliasUrl
 */
export const unifyAliasUrl = (offers, capacity, color) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    let aliasUrlUnify = null;
    Object.entries(mainOffer).forEach(([key]) => {
      if (
        mainOffer[key][0] &&
        mainOffer[key][0].aliasUrl &&
        mainOffer[key][0].aliasUrl !== aliasUrlUnify
      ) {
        aliasUrlUnify = mainOffer[key][0].aliasUrl;
      }
    });
    return aliasUrlUnify;
  }
  return null;
};

export const unifyCustomAliasUrl = (offers, capacity, color) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    let customAliasUrlUnify = null;
    Object.entries(mainOffer).forEach(([key]) => {
      if (
        mainOffer[key][0] &&
        mainOffer[key][0].customAliasUrl &&
        mainOffer[key][0].customAliasUrl !== customAliasUrlUnify
      ) {
        customAliasUrlUnify = mainOffer[key][0].customAliasUrl;
      }
    });
    return customAliasUrlUnify;
  }
  return null;
};

/**
 * Recibe la oferta, la capacidad y el color
 * Recorre cada tipo de oferta (r2r, r2rAdicional, libre, reacondicionados) y unifica el nombre
 * @param {Object} offers
 * @param {string} capacity
 * @param {string} color
 * @returns String con el nombre del dispositivo
 */
export const unifyName = (offers, capacity, color) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    let nameUnify = null;
    Object.entries(mainOffer).forEach(([key]) => {
      if (
        mainOffer[key][0] &&
        mainOffer[key][0].name &&
        mainOffer[key][0].name !== nameUnify
      ) {
        if (!nameUnify || nameUnify.toLowerCase().includes("reacondicionad") || !mainOffer[key][0].name.toLowerCase().includes("reacondicionad")) {
          nameUnify = mainOffer[key][0].name;
        }
      }
    });
    return nameUnify;
  }
  return null;
};

/**
 * Recibe la oferta, la capacidad y el color
 * Recorre cada tipo de oferta (r2r, r2rAdicional, libre, reacondicionados) y unifica el color
 * @param {Object} offers
 * @param {string} capacity
 * @param {string} color
 * @returns String con el color unificado
 */
export const unifyColor = (offers, capacity, color) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    let colorUnify = null;
    Object.entries(mainOffer).forEach(([key]) => {
      if (
        mainOffer[key][0] &&
        mainOffer[key][0].colorName &&
        mainOffer[key][0].colorName !== colorUnify
      ) {
        colorUnify = mainOffer[key][0].colorName;
      }
    });
    return colorUnify;
  }
  return null;
};

/**
 * Recibe la oferta, la capacidad y el color
 * Recorre cada tipo de oferta (r2r, r2rAdicional, libre, reacondicionados) y unifica las imagenes en un array
 * @param {Object} offers
 * @param {string} capacity
 * @param {string} color
 * @returns Array de imagenes unificadas
 */
export const unifyImages = (offers, capacity, color) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    const images = [];

    Object.entries(mainOffer).forEach(([key]) => {
      if (mainOffer[key][0] && mainOffer[key][0].imgs) {
        mainOffer[key][0].imgs.forEach((img) => {
          if (img && !images.includes(`https://www.movistar.es${img}`)) {
            images.push(`https://www.movistar.es${img}`)
          }
        });
      }
    });
    return images;
  }
  return null;
};

/**
 * Recibe la oferta, la capacidad y el color
 * Recorre cada tipo de oferta (r2r, r2rAdicional, libre, reacondicionados) y unifica el color
 * @param {Object} offers
 * @param {string} capacity
 * @param {string} color
 * @returns String con el color unificado
 */
export const unifyStamps = (offers, capacity, color) => {
  if (offers && capacity && color && offers[capacity][color]) {
    const mainOffer = offers[capacity][color];
    let stampUnify = {};
    Object.entries(mainOffer).forEach(([key]) => {
      if (
        mainOffer[key][0] &&
        mainOffer[key][0].stamps &&
        mainOffer[key][0].stamps[0].text &&
        mainOffer[key][0].stamps[0].text !== stampUnify
      ) {
        stampUnify.text = mainOffer[key][0].stamps[0].text;
        stampUnify.backgroundColor = mainOffer[key][0].stamps[0].kind;
      }
    });
    return stampUnify;
  }
  return null;
};

/**
 * Recibe el Objeto de ofertas y devuelve un array con las capacidades
 * @param {Object} offers
 * @returns Array de capacidades
 */
export const getCapacityKeys = (offers) => {
  if (offers) {
    const capacities = [];
    Object.entries(offers).forEach(([key]) => {
      capacities.push(key);
    });
    return capacities;
  }
  return null;
};

/**
 * Recibe las ofertas de un dispositivo, y devuelve la variante de cada oferta más barata
 * @param {Object} offers
 * @returns Objeto con la oferta normalizada
 */
export const getMainOffers = (offers) => {
  if (offers) {
    const objectOffer = {};
    Object.entries(offers).forEach(([key]) => {
      if (offers[key].length !== 0 && key !== "colorName") {
        const cheapestOffer = offers[key].reduce((cheapest, current) => {
          return current.currentPrice < cheapest.currentPrice ? current : cheapest;
        });
        objectOffer[key] = [{
          ...cheapestOffer
        }];
      }
    });
    return objectOffer;
  }
  return null;
};

/**
 * Esta función recibe el array con todos los dispositivos de crossSelling, recorre dicho array, y devuelve los dispositivos normalizados
 * @param {Array} crossSelling
 * @returns Array con los dispositivos de crossSelling normalizados y con los parametros nencesarios para pintar las cards
 */
export const getCrossSelling = (crossSelling) => {
  if (crossSelling) {
    const arrayCrossSelling = [];
    crossSelling.map((product) => {
      if (product.offers && Object.keys(product.offers) && Object.keys(product.offers).length > 0) {
        const capacity = Object.keys(product.offers)[0];
        const color = Object.keys(product.offers[capacity])[0];
        const offersCS = getMainOffers(product.offers[capacity][color]);
        const productCS = {
          alias: product.aliasProducto,
          name: product?.info.name,
          type: product?.info?.type,
          offers: offersCS,
        };
        arrayCrossSelling.push(productCS);
      }
    });
    return arrayCrossSelling;
  }
  return null;
};

export const getProductSpecs = (offers) => {
  let productSpecs = null;
  if (offers) {
    Object.entries(offers).forEach(([key]) => {
      if (
        offers[key] &&
        Array.isArray(offers[key]) &&
        offers[key].length > 0 &&
        !productSpecs
      ) {
        const product = offers[key].find(
          (product) => product.productSpecs !== null
        );
        if (product) {
          productSpecs = product.productSpecs;
        }
      }
    });
  }
  return productSpecs;
};

export const getTradeIn = (offer) => {
  let tradeIn = null;

  if (offer) {
    const {
      libre,
      r2r,
      r2rAdditional,
      r2rAdditionalReacondicionado,
      r2rReacondicionado,
      reacondicionado
    } = offer;

    const firstModalityNonEmpty = [r2r, r2rReacondicionado, r2rAdditional, r2rAdditionalReacondicionado, libre, reacondicionado]
      // Existe calloutDesc a nivel de producto. calloutBuyBoxDesc se pinta a nivel de oferta, en la buy-card
      .find(modality =>
        Array.isArray(modality) &&
        modality.length > 0 &&
        modality[0].tradeIn
      );

    if (firstModalityNonEmpty) {
      const offerTradeIn = firstModalityNonEmpty[0].tradeIn;

      if (offerTradeIn) {
        tradeIn = {
          legalTerms: offerTradeIn.legalTerms || null,
          calloutDesc: offerTradeIn.calloutDesc || null,
          calloutBuyBoxDesc: offerTradeIn.calloutBuyBoxDesc || null,
          startDate: offerTradeIn.startDate || null,
          endDate: offerTradeIn.endDate || null,
        };
      }
    }
  }
  return tradeIn;
};

/**
  *Recorre todas las ofertas en busca de una tag que haga match con las stamps de custo
  *Con que haya una, ya se incluye la stamp, independientemente del tipo de oferta que le 
  *aplique al cliente
**/
export const getImageStamps = (offer, custoStamps) => {
  let stamp = null;

  if (offer && offer.cards) {
    const {
      cardFree,
      cardR2R,
      cardAditionalR2R
    } = offer.cards;

    if (Array.isArray(custoStamps) && custoStamps.length > 0) {
      const stampToApply = custoStamps.find((custoStamp) => {
        return [cardFree, cardR2R, cardAditionalR2R]
          .find(modality => Array.isArray(modality) && modality.length > 0 && modality.find(item => Array.isArray(item.tags) && item.tags.find(tag => custoStamp.tag === tag)));
      });
      if (stampToApply) {
        stamp = stampToApply;
      }
    }

  }
  return stamp;
};

export const getProductsOffering = (offers) => {
  const productsOffering = [];
  if (offers) {
    Object.entries(offers).forEach(([key]) => {
      if (offers[key] && Array.isArray(offers[key]) && offers[key].length > 0) {
        const product = offers[key].find((product) => product.productOffering !== null);
        if (product) {
          productsOffering.push(product.productOffering)
        }
      }
    });
  }
  return productsOffering;
};

const isValidOffersStructure = (offers) => {
  if (offers && typeof offers === 'object') {
    let isValidOffersObject = true;
    //Check object has as least one capacity and color
    if (Object.keys(offers).length === 0) {
      isValidOffersObject = false;
    }
    Object.values(offers).forEach(capacities => {
      if (Object.keys(capacities).length === 0) {
        isValidOffersObject = false;
      }
    })
    return isValidOffersObject;
  } else {
    return false;
  }
}

const getProductCharacteristics = (info) => {
  const { backsideCamera, processor, screen, battery, storage, ram, esim, esimUrl } = info.phoneCaracs;
  const brand = info.metadata.brand || null;
  const brandLink = info.deviceBrandSiteLink || null;
  const name = info.deviceModel || null;
  const nameLink = info.deviceModelSiteLink || null;
  const version = info.deviceVersion || null;
  const versionLink = info.deviceVersionSiteLink || null;
  const deviceRefurbishedText = info.deviceRefurbishedText || null;
  const deviceRefurbishedSiteLink = info.deviceRefurbishedSiteLink || null;
  const deviceRefurbishedCertifiedText = info.deviceRefurbishedCertifiedText || null;
  const deviceRefurbishedCertifiedSiteLink = info.deviceRefurbishedCertifiedSiteLink || null;

  return [
    { iconId: "movil-planta-m", label: "", value: deviceRefurbishedText, link: deviceRefurbishedSiteLink, hideLink: true },
    { iconId: "apple-ciclo-m", label: "", value: deviceRefurbishedCertifiedText, link: deviceRefurbishedCertifiedSiteLink, hideLink: true },
    { iconId: "movil-m", label: "Marca", value: brand, link: brandLink, hideLink: true },
    { iconId: "movil-m", label: "Categoría", value: name, link: nameLink, hideLink: true },
    { iconId: "movil-m", label: "Modelo", value: version, link: versionLink, hideLink: true },
    { iconId: "configuracion-m", label: "Procesador", value: processor },
    { iconId: "camara-m", label: "Cámara principal", value: backsideCamera },
    { iconId: "movil-m", label: "Pantalla", value: screen },
    { iconId: "bateria-m", label: "Tipo de batería", value: battery },
    { iconId: "procesador-m", label: "Memoria interna", value: storage },
    { iconId: "procesador-m", label: "RAM", value: ram },
    { iconId: "esim-m", label: "eSIM", value: esim, link: esimUrl },
  ].filter((charac) =>
    //No mostrar apple reacon
    charac.value &&
    !(charac.label === "Marca" &&
      charac.value.includes("Apple") &&
      charac.value.includes("reacondicionado")
    ));
}

/**
 * Esta función recibe los datos de la api getItem y los normaliza
 * Primero normaliza las capacidades para que los convierta en TB en caso de que sea necesario
 * Ordena el objeto de manor capacidad a mayor capacidad
 * Normaliza las ofertas para unificar: imagenes, nombre, color y cards de producto
 * @param {string} response
 * @returns devuelve los detalles del terminal ya normalizados
 */
export const normalizeSingleProduct = ({ catalogueResponse, custoStamps, currentEnv }) => {
  const { crossSelling, crossSellingRTR, crossSellingRTRAdditional, ...info } = catalogueResponse.info;
  const crossSellingNormalize = getCrossSelling(crossSelling);
  const crossSellingRTRNormalize = getCrossSelling(crossSellingRTR);
  const crossSellingRTRAdditionalNormalize = getCrossSelling(crossSellingRTRAdditional);
  let data = {
    aliasProducto: catalogueResponse.aliasProducto,
    info: {
      ...info,
      urlVideo: info.urlVideo ? getFullAEMStaticSrc(info.urlVideo, currentEnv) : '',
      deviceCharacteristics: getProductCharacteristics(info)
    },
    crossSelling: crossSellingNormalize,
    crossSellingRTR: crossSellingRTRNormalize,
    crossSellingRTRAdditional: crossSellingRTRAdditionalNormalize,
    offers: {}
  };
  if (catalogueResponse && Array.isArray(Object.keys(catalogueResponse.offers)) &&
    Object.keys(catalogueResponse.offers).length > 0) {
    const { offers } = catalogueResponse;
    const normalizeOffer = {};
    Object.entries(offers).forEach(([key]) => {
      normalizeOffer[normalizeCapacity(key)] = offers[key];
    });
    const sortedKeys = Object.keys(normalizeOffer).sort(compareCapacities);
    const sortedOffers = {};
    sortedKeys.forEach(function sortedKeysFunc(key) {
      sortedOffers[key] = normalizeOffer[key];
    });

    const capacities = getCapacityKeys(sortedOffers);
    const objectOffer = {};
    capacities.map((capacity) => {
      objectOffer[capacity] = {};
      Object.entries(sortedOffers[capacity]).forEach(([key]) => {
        const aliasUrl = unifyAliasUrl(sortedOffers, capacity, key);
        const customAliasUrl = unifyCustomAliasUrl(sortedOffers, capacity, key);
        const name = unifyName(sortedOffers, capacity, key);
        const color = unifyColor(sortedOffers, capacity, key);
        const images = unifyImages(sortedOffers, capacity, key);
        const stamp = unifyStamps(sortedOffers, capacity, key);
        const cards = composeCardByClientType(sortedOffers, capacity, key, custoStamps);
        const productSpecs = getProductSpecs(sortedOffers[capacity][key]);
        const productsOffering = getProductsOffering(sortedOffers[capacity][key]);
        const tradeIn = getTradeIn(sortedOffers[capacity][key]);

        objectOffer[capacity][key] = {
          aliasUrl,
          customAliasUrl,
          name,
          color,
          colorName: sortedOffers[capacity][key].colorName,
          stamp,
          images,
          cards,
          productSpecs,
          productsOffering,
          tradeIn
        };
      });
      return objectOffer[capacity];
    });
    const validOffers = isValidOffersStructure(objectOffer);
    if (!validOffers) {
      throw Error(`Hay ofertas del terminal ${data.aliasProducto} con un estado inconsistente`)
    }
    data.offers = objectOffer;
    return data;
  }
  return data;
};

/**
 * Esta función recibe los datos de la api getAllItem para los productos relacionados y los normaliza
 * para mostrarse como cards de dispositivos
 * @param {string} response
 * @returns devuelve los terminales relacionados ya normalizados
 */
export const normalizeRelatedProducts = (response) => {
  const normalizedRelatedProducts = {
    products: response.products.map(product => ({
      brand: product.metadata.brand,
      name: product.name,
      type: product.type,
      offers: getMainOffers(product.offers)
    }))
  }
  return normalizedRelatedProducts;
}

/**
 * Recibe dos objetos y los combina, quitando los valores nulos y actualizando los que son distintos
 * @param {Object} obj1 Primer Objeto
 * @param {Object} obj2 Segundo Obejeto
 * @returns Objeto combinado
 */
export const combineObjects = (obj1, obj2) => {
  const combinedObj = {};

  Object.entries(obj1).forEach(([key, value1]) => {
    const value2 = obj2[key];

    if ((value1 === null && value2 !== undefined) || value1 !== value2) {
      combinedObj[key] = value2;
    } else {
      combinedObj[key] = value1;
    }
  });

  return combinedObj;
};

/**
 * Función que recibe la oferta según una capacidad, y en base al color, obtenemos el color en hexadecimal
 * @param {Object} offer
 * @param {string} color
 * @returns Color en hexadecimal
 */
export const getHexColor = (offer, color) => {
  if (offer && color) {
    let colorHex = null;
    Object.entries(offer).forEach(([key]) => {
      if (
        offer[key].colorName
          .toUpperCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f ]/g, "") ===
        color.toUpperCase().replace(" ", "")
      ) {
        colorHex = key;
      }
    });
    return colorHex;
  }
  return null;
};

export const getProductName = (capacity, color, offers) => {
  let nameProduct = '';
  if (capacity && color && offers) {
    const product = offers[capacity][
      color
    ];
    if (product) {
      nameProduct = product.name;
    }
  }
  return nameProduct;
}
